'use client';

import { useCallback, useMemo } from 'react';
import { useSPARouter, useSPAPathname, useSPALocation, useSPAParams } from '../context/SPARouterContext';

// Re-export Link as the lang-aware LangLink
export { default as Link } from '@/components/common/LangLink';

const isAuthPath = (path) => /^\/login(?:[/?#]|$)/.test(path);
const isAnyLoginPath = (path) => /^\/(?:[a-z]{2}-[a-z]{2}\/)?login(?:[/?#]|$)/i.test(path);

const withLoginRedirect = (url) => {
  if (typeof window === 'undefined') return url;
  try {
    const u = new URL(url, window.location.origin);
    if (!isAnyLoginPath(u.pathname)) return u.pathname + u.search + u.hash;
    if (u.searchParams.has('redirect')) return u.pathname + u.search + u.hash;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (isAnyLoginPath(window.location.pathname)) return u.pathname + u.search + u.hash;
    u.searchParams.set('redirect', current);
    return u.pathname + u.search + u.hash;
  } catch {
    return url;
  }
};

const hardNavigate = (url, shouldReplace = false) => {
  if (typeof window === 'undefined') return;
  const finalUrl = withLoginRedirect(url);
  if (shouldReplace) {
    window.location.replace(finalUrl);
  } else {
    window.location.assign(finalUrl);
  }
};

const isOutsideMainSPA = () => (
  typeof document !== 'undefined' && !document.getElementById('main-content')
);

// useParams - returns route params from SPA router
export const useParams = () => {
  return useSPAParams();
};

// useNavigate - uses pushState, no server request
export const useNavigate = () => {
  const { push, back, replace, lang } = useSPARouter();

  return useCallback((to, options) => {
    if (typeof to === 'number') {
      if (to === -1) back();
      return;
    }
    if (typeof to === 'string' && to.startsWith('/')) {
      const prefixed = `/${lang}${to}`;

      // /login lives in Next auth route, not in AppRouter route table.
      // Use hard navigation to avoid client-side 404.
      if (isAuthPath(to)) {
        hardNavigate(prefixed, options?.replace);
        return;
      }

      // When called from auth pages (outside MainLayout/AppRouter),
      // pushState only changes URL and does not switch route trees.
      if (isOutsideMainSPA()) {
        hardNavigate(prefixed, options?.replace);
        return;
      }

      if (options?.replace) {
        replace(prefixed);
      } else {
        push(prefixed);
      }
    } else {
      push(to);
    }
  }, [push, back, replace, lang]);
};

// useLocation - returns location-like object
export const useLocation = () => {
  const pathname = useSPAPathname();
  const { lang } = useSPARouter();
  const stripped = pathname?.startsWith(`/${lang}`) ? pathname.slice(lang.length + 1) || '/' : pathname;

  return {
    pathname: stripped,
    search: typeof window !== 'undefined' ? window.location.search : '',
    hash: typeof window !== 'undefined' ? window.location.hash : '',
  };
};

// useSearchParams - reads/writes URL search params
export const useSearchParams = () => {
  // useSPALocation tracks pathname+search so re-renders on any URL change
  const location = useSPALocation();
  const { push, replace } = useSPARouter();

  // Extract search string from tracked location to ensure fresh value on each change
  const searchString = location.includes('?') ? '?' + location.split('?')[1] : '';
  const searchParams = useMemo(() => new URLSearchParams(searchString), [searchString]);

  const setSearchParams = useCallback((newParams, options) => {
    let sp;
    if (newParams instanceof URLSearchParams) {
      sp = newParams;
    } else if (typeof newParams === 'object') {
      sp = new URLSearchParams();
      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          sp.set(key, value);
        }
      });
    } else {
      sp = new URLSearchParams(newParams);
    }

    const queryString = sp.toString();
    const currentPath = window.location.pathname;
    const url = queryString ? `${currentPath}?${queryString}` : currentPath;
    if (options?.replace) {
      replace(url);
    } else {
      push(url);
    }
  }, [push, replace]);

  return [searchParams, setSearchParams];
};
